import { RoomSession } from './Room'
import {
  ADVISOR_REFERRAL_PRESET_IDS,
  DECISION_TRACKER_PRESET_IDS,
  FUTURE_MEETINGS_PRESET_IDS,
  LIST_OF_LIFE_EVENTS_PRESET_IDS,
  NON_FINANCIAL_PERSONAL_INFORMATION_PRESET_IDS,
} from '../views/MeetingSummary/CRM/PresetMappings'
import { IntegrationEmail } from './Email'

const env = import.meta.env.REACT_APP_ENV! as 'prod' | 'staging' | 'dev'

export const BusinessEventSourceType = { AI_FEATURE_EXECUTION: 'aiFeatureExecution', EMAIL: 'email' } as const

export type BusinessEventSourceTypeValues = (typeof BusinessEventSourceType)[keyof typeof BusinessEventSourceType]

export interface AIFeatureExecutionBusinessEventSource {
  type: typeof BusinessEventSourceType.AI_FEATURE_EXECUTION
  roomId: string
  sessionId: string
  aiFeatureId: string
}

export interface EmailBusinessEventSource {
  type: typeof BusinessEventSourceType.EMAIL
  emailConnector: string
  threadId: string
  emailId: string
}

export type BusinessEventSource = AIFeatureExecutionBusinessEventSource | EmailBusinessEventSource

export const BUSINESS_EVENT_SOURCE_ICON_MAPPING: Record<BusinessEventSourceTypeValues, string> = {
  [BusinessEventSourceType.AI_FEATURE_EXECUTION]: 'bi-calendar',
  [BusinessEventSourceType.EMAIL]: 'bi-envelope',
}

export type BusinessEventDetailsValueType = string | number | boolean | null | string[]

export const BusinessEventType = {
  REFERRAL: 'referral',
  MARRIAGE: 'marriage',
  WIDOWING: 'widowing',
  DIVORCE: 'divorce',
  CHILD_BIRTH: 'childBirth',
  PROPERTY_PURCHASE: 'propertyPurchase',
  PROPERTY_SALE: 'propertySale',
  JOB_CHANGE: 'jobChange',
  RETIREMENT: 'retirement',
  INHERITANCE: 'inheritance',
  BUSINESS_START: 'businessStart',
  HEALTH_ISSUE: 'healthIssue',
  FAMILY_MEMBER_HEALTH_ISSUE: 'familyMemberHealthIssue',
  CHILDREN_STARTING_COLLEGE: 'childrenStartingCollege',
  DECISION_MADE: 'decisionMade',
  FUTURE_MEETING: 'futureMeeting',
  HOBBY: 'hobby',
  INTEREST: 'interest',
  MEMBERSHIP: 'membership',
  RELIGION: 'religion',
  TRAVELING: 'traveling',
  MOVING: 'moving',
  PET: 'pet',
} as const

export type BusinessEventType = (typeof BusinessEventType)[keyof typeof BusinessEventType]

export interface BusinessEvent {
  id: string
  type: BusinessEventType
  details: Record<string, BusinessEventDetailsValueType>
  source: BusinessEventSource
  at: string | null
  createdAt: string
}

export const BUSINESS_EVENT_LABEL_MAP: Record<BusinessEventType, string> = {
  [BusinessEventType.REFERRAL]: 'Referral',
  [BusinessEventType.MARRIAGE]: 'Marriage',
  [BusinessEventType.WIDOWING]: 'Widowed',
  [BusinessEventType.DIVORCE]: 'Divorce',
  [BusinessEventType.CHILD_BIRTH]: 'Birth of a Child',
  [BusinessEventType.PROPERTY_PURCHASE]: 'Bought property',
  [BusinessEventType.PROPERTY_SALE]: 'Sold property',
  [BusinessEventType.JOB_CHANGE]: 'Job change',
  [BusinessEventType.RETIREMENT]: 'Retirement',
  [BusinessEventType.INHERITANCE]: 'Received Inheritance',
  [BusinessEventType.BUSINESS_START]: 'Started a new Business',
  [BusinessEventType.HEALTH_ISSUE]: 'Major Health Issue',
  [BusinessEventType.FAMILY_MEMBER_HEALTH_ISSUE]: 'Family Member Health Issue',
  [BusinessEventType.CHILDREN_STARTING_COLLEGE]: 'Children Starting College',
  [BusinessEventType.DECISION_MADE]: 'Decision made',
  [BusinessEventType.FUTURE_MEETING]: 'Future Meeting',
  [BusinessEventType.INTEREST]: 'Interest detected',
  [BusinessEventType.HOBBY]: 'Hobby detected',
  [BusinessEventType.MEMBERSHIP]: 'Membership detected',
  [BusinessEventType.RELIGION]: 'Religion detected',
  [BusinessEventType.TRAVELING]: 'Traveling',
  [BusinessEventType.MOVING]: 'Moving / relocation',
  [BusinessEventType.PET]: 'Pet detected',
}

export interface BaseResolvedBusinessEventSource {
  sourceKey: string
}

export interface AIFeatureExecutionResolvedBusinessEventSource extends BaseResolvedBusinessEventSource {
  type: typeof BusinessEventSourceType.AI_FEATURE_EXECUTION
  source: RoomSession
}

export interface EmailResolvedBusinessEventSource extends BaseResolvedBusinessEventSource {
  type: typeof BusinessEventSourceType.EMAIL
  source: IntegrationEmail
}

export type ResolvedBusinessEventSource = AIFeatureExecutionResolvedBusinessEventSource | EmailResolvedBusinessEventSource

export const BUSINESS_EVENT_TYPES_PER_PRESET_ID_MAPPING: { [key: string]: BusinessEventType[] } = {
  [FUTURE_MEETINGS_PRESET_IDS[env]]: [BusinessEventType.FUTURE_MEETING],
  [DECISION_TRACKER_PRESET_IDS[env]]: [BusinessEventType.DECISION_MADE],
  [LIST_OF_LIFE_EVENTS_PRESET_IDS[env]]: [
    BusinessEventType.MARRIAGE,
    BusinessEventType.DIVORCE,
    BusinessEventType.WIDOWING,
    BusinessEventType.CHILD_BIRTH,
    BusinessEventType.PROPERTY_PURCHASE,
    BusinessEventType.PROPERTY_SALE,
    BusinessEventType.JOB_CHANGE,
    BusinessEventType.INHERITANCE,
    BusinessEventType.BUSINESS_START,
    BusinessEventType.HEALTH_ISSUE,
    BusinessEventType.FAMILY_MEMBER_HEALTH_ISSUE,
    BusinessEventType.CHILDREN_STARTING_COLLEGE,
    BusinessEventType.RETIREMENT,
  ],
  [ADVISOR_REFERRAL_PRESET_IDS[env]]: [BusinessEventType.REFERRAL],
  [NON_FINANCIAL_PERSONAL_INFORMATION_PRESET_IDS[env]]: [
    BusinessEventType.HOBBY,
    BusinessEventType.INTEREST,
    BusinessEventType.MEMBERSHIP,
    BusinessEventType.RELIGION,
    BusinessEventType.TRAVELING,
    BusinessEventType.MOVING,
    BusinessEventType.PET,
  ],
}

export const BUSINESS_EVENT_DETAILS_KEYS_MAPPING: Partial<Record<BusinessEventType, string[]>> = {
  [BusinessEventType.DECISION_MADE]: ['decision', 'agreedToDo'],
}
